<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── BACK ── -->
      <button class="back-btn" @click="$router.back()">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Back to directory
      </button>

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Loading profile…</p>
      </div>

      <template v-else>

        <!-- ── HERO ── -->
        <div class="hero">
          <div class="hero-avatar-wrap">
            <div class="hero-avatar-ring"></div>
            <div class="hero-avatar-spin"></div>
            <div class="hero-avatar">
              <img
                v-if="user.image && user.image !== defaultImage"
                :src="user.image"
                :alt="user.fullName"
              />
              <span v-else>{{ initials }}</span>
            </div>
          </div>

          <p class="hero-eyebrow">{{ user.role || user.type || 'Member' }}</p>
          <h1 class="hero-name">{{ firstName }} <em>{{ restName }}</em></h1>
          <p class="hero-title">{{ user.jobTitle || '' }}</p>

          <div v-if="clubList.length" class="hero-club">
            <div class="hero-club-dot"></div>
            {{ clubList[0].name }}<span v-if="clubList.length > 1"> +{{ clubList.length - 1 }} more</span>
            <div class="hero-club-dot"></div>
          </div>

          <button
            v-if="!isConnected"
            class="btn-connect"
            :disabled="isRequestingIntro"
            @click="requestIntroduction"
          >
            <div class="btn-tick"></div>
            {{ isRequestingIntro ? 'Sending…' : 'Request Introduction' }}
            <div class="btn-tick"></div>
          </button>
          <button v-else class="btn-connect btn-connect--connected" @click="$router.push('/messages')">
            <div class="btn-tick"></div>
            <span>Connected · Message</span>
            <div class="btn-tick"></div>
          </button>
        </div>

        <!-- ── SPARSE PROFILE PLACEHOLDER ── -->
        <!-- Shown only when every optional section is empty, so the page
             reads as "this member hasn't filled anything in yet" rather
             than looking broken or unfinished. -->
        <div v-if="!hasExtendedProfile" class="sparse-note">
          <div class="sparse-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
          </div>
          <p class="sparse-text">
            {{ firstName || "This member" }} hasn't added a job title, city, club affiliation, or bio yet.
          </p>
        </div>

        <!-- ── CLUB AFFILIATION ── -->
        <div class="info-section club-section" v-if="clubList.length">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap club-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
            </div>
            <span class="info-eyebrow club-eyebrow">Club Affiliation</span>
            <div class="info-rule"></div>
          </div>
          <div class="club-list">
            <div v-for="(clubEntry, idx) in clubList" :key="idx" class="club-card">
              <div class="club-card-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
              </div>
              <div class="club-card-body">
                <p class="club-card-name">{{ clubEntry.name }}</p>
                <p v-if="clubEntry.membershipNumber" class="club-card-membership">Member No. {{ clubEntry.membershipNumber }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- ── CONTACT ── -->
        <div class="info-section">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.68A2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92z"/></svg>
            </div>
            <span class="info-eyebrow">Contact</span>
            <div class="info-rule"></div>
          </div>
          <div class="info-block">
            <div v-if="user.email" class="info-row">
              <span class="info-key">Email</span>
              <span class="info-val">{{ user.email }}</span>
            </div>
            <div v-if="user.phoneNumber" class="info-row">
              <span class="info-key">Phone</span>
              <span class="info-val">{{ user.phoneNumber }}</span>
            </div>
            <div v-if="user.gender" class="info-row">
              <span class="info-key">Gender</span>
              <span class="info-val">{{ user.gender }}</span>
            </div>
          </div>
        </div>

        <!-- ── PROFESSIONAL ── -->
        <div class="info-section" v-if="user.jobTitle || user.company">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>
            </div>
            <span class="info-eyebrow">Professional</span>
            <div class="info-rule"></div>
          </div>
          <div class="info-block">
            <div v-if="user.jobTitle" class="info-row">
              <span class="info-key">Job Title</span>
              <span class="info-val">{{ user.jobTitle }}</span>
            </div>
            <div v-if="user.company" class="info-row">
              <span class="info-key">Company</span>
              <span class="info-val">{{ user.company }}</span>
            </div>
          </div>
        </div>

        <!-- ── TRAVEL ── -->
        <div class="info-section" v-if="user.baseCity || frequentCitiesDisplay">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0119 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.3"/></svg>
            </div>
            <span class="info-eyebrow">Travel</span>
            <div class="info-rule"></div>
          </div>
          <div class="info-block">
            <div v-if="user.baseCity" class="info-row">
              <span class="info-key">Home Base</span>
              <span class="info-val">{{ user.baseCity }}</span>
            </div>
            <div v-if="frequentCitiesDisplay" class="info-row">
              <span class="info-key">Frequent Cities</span>
              <span class="info-val">{{ frequentCitiesDisplay }}</span>
            </div>
          </div>
        </div>

        <!-- ── BIO ── -->
        <div class="info-section" v-if="user.description">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>
            </div>
            <span class="info-eyebrow">About</span>
            <div class="info-rule"></div>
          </div>
          <div class="info-block bio-block">
            <p class="bio-text">{{ user.description }}</p>
          </div>
        </div>

        <!-- ── RECOMMENDATIONS ── -->
        <div class="info-section" v-if="posts.length">
          <div class="info-section-head">
            <div class="info-rule l"></div>
            <div class="info-icon-wrap">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
            </div>
            <span class="info-eyebrow">Recommendations</span>
            <div class="info-rule"></div>
          </div>
          <div class="posts-grid">
            <div
              v-for="post in posts"
              :key="post.id"
              class="post-card"
              @click="$router.push({ name: 'PostDetail', params: { postId: post.id } })"
            >
              <img v-if="post.image" :src="post.image" :alt="post.caption" class="post-img" />
              <div v-else class="post-img-placeholder">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
              </div>
              <div class="post-body">
                <p class="post-caption">{{ post.caption }}</p>
                <div class="post-actions">
                  <button class="post-action" @click.stop>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 2l2.9 6.26L21 9.27l-4.5 4.38L17.8 20 12 16.9 6.2 20l1.3-6.35L3 9.27l6.1-1.01L12 2z"/></svg>
                    <span>{{ post.likes || 0 }}</span>
                  </button>
                  <button class="post-action" @click.stop>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                    <span>{{ post.comments || 0 }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </template>
    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item active" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
        <div class="nav-dot"></div>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$router.push('/profile')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { doc, getDoc, collection, getDocs, query, where, addDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "@/firebase";

export default {
  name: "MemberDetail",

  data() {
    return {
      user: {},
      posts: [],
      isLoading: true,
      isRequestingIntro: false,
      isConnected: false,
      defaultImage: "@/assets/default-user.png",
    };
  },

  computed: {
    initials() {
      const parts = (this.user.fullName || "").trim().split(/\s+/);
      return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "M";
    },
    // frequentCities is stored as a real array; older accounts may still
    // have the legacy comma-separated string. Both need a single clean
    // display string — and just as importantly, an empty array is truthy
    // in JS ([] || x evaluates to [], and Boolean([]) is true), so using
    // the raw field directly in v-if or hasExtendedProfile incorrectly
    // treated an empty array as "has content." A string's emptiness
    // behaves correctly in both places.
    frequentCitiesDisplay() {
      const raw = this.user.frequentCities;
      if (Array.isArray(raw)) return raw.filter(Boolean).join(", ");
      return raw || "";
    },
    firstName() {
      return (this.user.fullName || "").trim().split(/\s+/)[0] || "";
    },
    restName() {
      const parts = (this.user.fullName || "").trim().split(/\s+/);
      return parts.slice(1).join(" ") || "";
    },
    // Prefer the full multi-club list; fall back to the single legacy
    // `club` string for accounts created before club affiliation supported
    // more than one entry.
    clubList() {
      // Handle `clubs` whether it's an array of {name, membershipNumber}
      // objects (the current write shape) or plain strings (in case data
      // got in there some other way).
      if (Array.isArray(this.user.clubs) && this.user.clubs.length) {
        return this.user.clubs
          .map(c => (typeof c === "string" ? { name: c } : c))
          .filter(c => c && c.name);
      }
      // Fallback for accounts with only the legacy single `club` field —
      // split on commas in case multiple club names were saved into that
      // one string field before the multi-club list existed.
      if (this.user.club) {
        return this.user.club
          .split(",")
          .map(name => name.trim())
          .filter(Boolean)
          .map(name => ({ name }));
      }
      return [];
    },
    // True once this member has filled in at least one of the optional
    // sections. When false, the page would otherwise silently render
    // nothing between the hero and Contact — indistinguishable from a
    // broken page rather than "this person hasn't added details yet."
    hasExtendedProfile() {
      return Boolean(
        this.user.jobTitle ||
        this.user.company ||
        this.user.baseCity ||
        this.frequentCitiesDisplay ||
        this.user.description ||
        this.clubList.length ||
        this.posts.length
      );
    },
  },

  async mounted() {
    await this.fetchUser();
    await this.checkConnectionStatus();
  },

  methods: {
    async checkConnectionStatus() {
      const currentUser = auth.currentUser;
      const targetUid = this.$route.params.id;
      if (!currentUser || !targetUid) return;
      try {
        const connSnap = await getDoc(doc(db, "user", currentUser.uid, "connections", targetUid));
        this.isConnected = connSnap.exists();
      } catch (error) {
        console.error("Failed to check connection status:", error);
      }
    },

    async fetchUser() {
      this.isLoading = true;
      try {
        const userId = this.$route.params.id;
        const userRef = doc(db, "user", userId);
        const userSnapshot = await getDoc(userRef);
        if (userSnapshot.exists()) {
          this.user = userSnapshot.data();
        } else {
          console.error("User not found.");
        }
        await this.fetchPosts(userId);
      } catch (error) {
        console.error("Error fetching user:", error);
      } finally {
        this.isLoading = false;
      }
    },

    // Pulls this member's public Explore recommendations. (The old version of
    // this method read a `user/{id}/posts` subcollection that nothing in the
    // app writes to anymore, and had no Firestore rule — that's what was
    // producing the permission-denied error.)
    async fetchPosts(userId) {
      try {
        const q = query(
          collection(db, "explore"),
          where("uid", "==", userId),
          where("isPublic", "==", true)
        );
        const postsSnap = await getDocs(q);
        this.posts = postsSnap.docs.map(d => {
          const data = d.data();
          const media = Array.isArray(data.media) && data.media.length
            ? data.media
            : data.imageURL
              ? [{ url: data.imageURL, type: "image" }]
              : [];
          const arrangementCount = Array.isArray(data.arrangementInterest) ? data.arrangementInterest.length : 0;
          return {
            id: d.id,
            ...data,
            image: media[0]?.url || null,
            caption: data.businessName || data.text || "",
            likes: arrangementCount,
            comments: data.commentsCount || 0,
          };
        });
      } catch (error) {
        console.error("Error fetching posts:", error);
        this.posts = [];
      }
    },

    // Sends a starter message and creates a mutual connection so the thread
    // actually shows up in Messages — ConversationView.vue's list is built
    // entirely from each user's `connections` subcollection, so a message
    // alone (no connection doc) is invisible in the UI even though it's
    // saved. This creates the connection immediately, both directions —
    // it does NOT wait for the other person to accept anything. If you want
    // a real pending-request/accept flow instead of an instant connection,
    // that's a bigger feature to design; this is the minimal fix that makes
    // the button actually do what it says.
    async requestIntroduction() {
      const currentUser = auth.currentUser;
      const targetUid = this.$route.params.id;
      if (!currentUser || !targetUid || this.isRequestingIntro) return;

      this.isRequestingIntro = true;
      try {
        // Connection docs need fullName/image written directly on them —
        // HomeView.vue's connections list reads `...doc.data()` with no
        // secondary user lookup, so without these fields a new connection
        // shows up with no name or photo (just a blank "?").
        const myUserSnap = await getDoc(doc(db, "user", currentUser.uid));
        const myData = myUserSnap.exists() ? myUserSnap.data() : {};

        await Promise.all([
          setDoc(doc(db, "user", currentUser.uid, "connections", targetUid), {
            uid: targetUid,
            fullName: this.user.fullName || "",
            image: this.user.image || "",
            connectedAt: serverTimestamp(),
          }),
          setDoc(doc(db, "user", targetUid, "connections", currentUser.uid), {
            uid: currentUser.uid,
            fullName: myData.fullName || currentUser.displayName || "",
            image: myData.image || currentUser.photoURL || "",
            connectedAt: serverTimestamp(),
          }),
        ]);

        await addDoc(collection(db, "messages"), {
          senderId: currentUser.uid,
          receiverUid: targetUid,
          text: `Hi ${this.firstName || "there"} — I'd love an introduction. I saw your profile on The Inner Network.`,
          timestamp: new Date(),
          received: false,
          read: false,
        });

        this.isConnected = true;
        this.$router.push("/messages");
      } catch (error) {
        console.error("Failed to request introduction:", error);
      } finally {
        this.isRequestingIntro = false;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 860px; margin: 0 auto; padding: 48px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── BACK ── */
.back-btn { display: flex; align-items: center; gap: 10px; background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 10px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(237,232,223,0.25); transition: color 0.2s; margin-bottom: 40px; padding: 0; }
.back-btn:hover { color: rgba(210,175,100,0.6); }
.back-btn svg { width: 14px; height: 14px; }

/* ── LOADING ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 100px 0; }
.loading-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }

/* ── HERO ── */
.hero { display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 56px; padding-bottom: 48px; border-bottom: 1px solid rgba(210,175,100,0.1); }
.hero-avatar-wrap { position: relative; margin-bottom: 28px; }
.hero-avatar-ring { position: absolute; inset: -10px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.15); }
.hero-avatar-spin { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.1); animation: spinSlow 25s linear infinite; }
.hero-avatar { width: 120px; height: 120px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.3); overflow: hidden; background: rgba(210,175,100,0.06); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 42px; font-weight: 300; color: rgba(210,175,100,0.5); position: relative; z-index: 1; }
.hero-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.hero-eyebrow { font-size: 10px; font-weight: 600; letter-spacing: 3.5px; color: rgba(210,175,100,0.4); text-transform: uppercase; margin-bottom: 14px; }
.hero-name { font-family: 'Cormorant', serif; font-size: 56px; font-weight: 300; color: #edeae2; letter-spacing: -1px; line-height: 1; margin-bottom: 10px; }
.hero-name em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }
.hero-title { font-size: 13px; font-weight: 400; color: rgba(237,232,223,0.3); letter-spacing: 1px; margin-bottom: 24px; }
.hero-club { display: inline-flex; align-items: center; gap: 10px; border: 1px solid rgba(210,175,100,0.2); padding: 9px 22px; font-size: 10px; font-weight: 600; letter-spacing: 2.5px; color: rgba(210,175,100,0.55); text-transform: uppercase; margin-bottom: 32px; }
.hero-club-dot { width: 4px; height: 4px; border-radius: 50%; background: rgba(210,175,100,0.4); }

.btn-connect { display: flex; align-items: center; justify-content: center; gap: 14px; border: 1px solid rgba(210,175,100,0.35); background: rgba(210,175,100,0.07); padding: 18px 52px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 11px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: rgba(210,175,100,0.85); position: relative; overflow: hidden; transition: border-color 0.3s, opacity 0.2s; }
.btn-connect:disabled { opacity: 0.55; cursor: not-allowed; }
.btn-connect--connected { background: rgba(210,175,100,0.03); border-color: rgba(210,175,100,0.25); color: rgba(210,175,100,0.6); }
.btn-connect--connected:hover { border-color: rgba(210,175,100,0.5); }
.btn-connect::before { content: ''; position: absolute; inset: 0; background: rgba(210,175,100,0.06); transform: scaleX(0); transform-origin: left; transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94); }
.btn-connect:hover { border-color: rgba(210,175,100,0.65); }
.btn-connect:hover::before { transform: scaleX(1); }
.btn-connect:hover .btn-tick { width: 24px; }
.btn-tick { width: 18px; height: 1px; background: rgba(210,175,100,0.3); transition: width 0.3s; flex-shrink: 0; }

/* ── INFO SECTIONS ── */
.info-section { margin-bottom: 40px; }
.info-section-head { display: flex; align-items: center; gap: 16px; margin-bottom: 24px; }
.info-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.info-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.info-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.4); text-transform: uppercase; white-space: nowrap; }
.info-icon-wrap { width: 36px; height: 36px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); display: flex; align-items: center; justify-content: center; color: rgba(210,175,100,0.5); flex-shrink: 0; }
.info-icon-wrap svg { width: 15px; height: 15px; }

.info-block { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); padding: 8px 32px; position: relative; }
.bio-block { padding: 24px 32px; }
.bio-text { font-size: 14px; font-weight: 300; line-height: 1.8; color: rgba(237,232,223,0.55); letter-spacing: 0.2px; white-space: pre-wrap; }

/* ── SPARSE PROFILE PLACEHOLDER ── */
.sparse-note { display: flex; align-items: center; gap: 16px; border: 1px dashed rgba(210,175,100,0.15); background: rgba(210,175,100,0.02); padding: 22px 26px; margin-bottom: 40px; }
.sparse-icon { color: rgba(210,175,100,0.3); flex-shrink: 0; }
.sparse-icon svg { width: 22px; height: 22px; }
.sparse-text { font-size: 13px; font-weight: 300; font-style: italic; color: rgba(237,232,223,0.35); line-height: 1.6; }

/* ── CLUB AFFILIATION (elevated — this is the field that determines admission) ── */
.club-icon-wrap { border-color: rgba(210,175,100,0.4); color: rgba(210,175,100,0.8); }
.club-eyebrow { color: rgba(210,175,100,0.65); }
.club-list { display: flex; flex-direction: column; gap: 10px; }
.club-card { display: flex; align-items: center; gap: 16px; border: 1px solid rgba(210,175,100,0.22); background: rgba(210,175,100,0.04); padding: 18px 24px; position: relative; }
.club-card::before, .club-card::after { content: ''; position: absolute; width: 10px; height: 10px; }
.club-card::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.4); border-left: 1px solid rgba(210,175,100,0.4); }
.club-card::after { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.4); border-right: 1px solid rgba(210,175,100,0.4); }
.club-card-icon { width: 40px; height: 40px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.3); display: flex; align-items: center; justify-content: center; color: rgba(210,175,100,0.65); flex-shrink: 0; }
.club-card-icon svg { width: 17px; height: 17px; }
.club-card-body { flex: 1; min-width: 0; }
.club-card-name { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 400; color: #edeae2; line-height: 1.2; }
.club-card-membership { font-size: 10px; font-weight: 500; letter-spacing: 1px; color: rgba(210,175,100,0.5); text-transform: uppercase; margin-top: 4px; }
.info-block::before, .info-block::after { content: ''; position: absolute; width: 12px; height: 12px; }
.info-block::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.3); border-left: 1px solid rgba(210,175,100,0.3); }
.info-block::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.3); border-right: 1px solid rgba(210,175,100,0.3); }
.info-row { display: flex; align-items: flex-start; gap: 0; padding: 16px 0; border-bottom: 1px solid rgba(237,232,223,0.05); }
.info-row:last-child { border-bottom: none; }
.info-key { font-size: 9px; font-weight: 600; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); min-width: 140px; padding-top: 3px; flex-shrink: 0; }
.info-val { font-size: 15px; font-weight: 300; color: rgba(237,232,223,0.7); letter-spacing: 0.3px; }
.info-val--link { color: rgba(210,175,100,0.6); }

/* Chips */
.chip-row { display: flex; flex-wrap: wrap; gap: 8px; }
.tin-chip { padding: 5px 12px; border: 1px solid rgba(210,175,100,0.2); font-size: 11px; font-weight: 400; color: rgba(210,175,100,0.6); letter-spacing: 0.5px; }

/* ── POSTS ── */
.posts-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
.post-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; position: relative; overflow: hidden; }
.post-card::before { content: ''; position: absolute; top: -1px; left: -1px; width: 10px; height: 10px; border-top: 1px solid rgba(210,175,100,0.3); border-left: 1px solid rgba(210,175,100,0.3); z-index: 1; }
.post-img { width: 100%; height: 160px; object-fit: cover; display: block; filter: brightness(0.9); }
.post-img-placeholder { width: 100%; height: 160px; background: rgba(210,175,100,0.04); display: flex; align-items: center; justify-content: center; }
.post-img-placeholder svg { width: 24px; height: 24px; color: rgba(210,175,100,0.2); }
.post-body { padding: 14px 16px; }
.post-caption { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.45); line-height: 1.5; margin-bottom: 12px; }
.post-actions { display: flex; gap: 16px; }
.post-action { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 400; letter-spacing: 0.5px; color: rgba(237,232,223,0.2); background: none; border: none; cursor: pointer; font-family: 'Montserrat', sans-serif; transition: color 0.2s; }
.post-action:hover { color: rgba(210,175,100,0.5); }
.post-action svg { width: 14px; height: 14px; }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
.nav-dot { width: 3px; height: 3px; border-radius: 50%; background: rgba(210,175,100,0.7); margin-top: 1px; }

/* ── RESPONSIVE ── */
@media (max-width: 760px) {
  .page { padding: 36px 24px 120px; }
  .hero-name { font-size: 40px; }
  .posts-grid { grid-template-columns: repeat(2, 1fr); }
  .info-key { min-width: 100px; }
  .frame, .frame-inner { inset: 12px; }
}
@media (max-width: 500px) {
  .posts-grid { grid-template-columns: 1fr; }
  .hero-name { font-size: 32px; }
  .btn-connect { padding: 16px 32px; }
}
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>